import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { blue, orange, green, purple } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// 一级指标颜色配置 - 支持暗色模式
const getLevel1Colors = (theme: any) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    '决策': { 
      bg: isDark ? blue[900] : blue[50], 
      border: isDark ? blue[700] : blue[200], 
      text: isDark ? blue[100] : blue[900] 
    },
    '产出': { 
      bg: isDark ? green[900] : green[50], 
      border: isDark ? green[700] : green[200], 
      text: isDark ? green[100] : green[900] 
    },
    '过程': { 
      bg: isDark ? orange[900] : orange[50], 
      border: isDark ? orange[700] : orange[200], 
      text: isDark ? orange[100] : orange[900] 
    },
    '效益': { 
      bg: isDark ? purple[900] : purple[50], 
      border: isDark ? purple[700] : purple[200], 
      text: isDark ? purple[100] : purple[900] 
    }
  };
};

// 生成新的编码
const generateNewCodes = (sortedStructure: any[]) => {
  const level1CodeMap = { '决策': 'A', '过程': 'B', '产出': 'C', '效益': 'D' };
  const newCodes: any[] = [];
  
  sortedStructure.forEach((level1Group) => {
    const level1Code = level1CodeMap[level1Group.name as keyof typeof level1CodeMap];
    
    level1Group.level2Groups.forEach((level2Group: any, level2Index: number) => {
      const level2Code = `${level1Code}${level2Index + 1}`;
      
      level2Group.indicators.forEach((indicator: any, level3Index: number) => {
        const level3Code = `${level2Code}${String(level3Index + 1).padStart(2, '0')}`;
        newCodes.push({
          id: indicator.id,
          indicator_code: level3Code
        });
      });
    });
  });
  
  return newCodes;
};

// 构建初始排序结构
const buildInitialStructure = (indicators: any[]) => {
  // 按一级指标分组
  const level1Groups: any = {};
  indicators.forEach(indicator => {
    if (!level1Groups[indicator.level1_name]) {
      level1Groups[indicator.level1_name] = {};
    }
    if (!level1Groups[indicator.level1_name][indicator.level2_name]) {
      level1Groups[indicator.level1_name][indicator.level2_name] = [];
    }
    level1Groups[indicator.level1_name][indicator.level2_name].push(indicator);
  });

  // 转换为排序结构
  return Object.entries(level1Groups).map(([level1Name, level2Groups]: [string, any]) => ({
    id: `level1-${level1Name}`,
    name: level1Name,
    level2Groups: Object.entries(level2Groups).map(([level2Name, indicators]: [string, any]) => ({
      id: `level2-${level1Name}-${level2Name}`,
      name: level2Name,
      indicators: indicators.sort((a: any, b: any) => {
        // 按现有编码排序
        const aCode = a.indicator_code || '';
        const bCode = b.indicator_code || '';
        return aCode.localeCompare(bCode);
      })
    })).sort((a: any, b: any) => {
      // 按第一个三级指标的编码排序
      const aCode = a.indicators[0]?.indicator_code || '';
      const bCode = b.indicators[0]?.indicator_code || '';
      return aCode.localeCompare(bCode);
    })
  })).sort((a: any, b: any) => {
    // 按一级指标固定顺序：决策、过程、产出、效益
    const order = { '决策': 0, '过程': 1, '产出': 2, '效益': 3 };
    return order[a.name as keyof typeof order] - order[b.name as keyof typeof order];
  });
};

// 可排序的指标组件
function SortableIndicator({ indicator }: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: indicator.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        p: 1,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing',
        },
      }}
      {...attributes}
      {...listeners}
    >
      <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" fontWeight="bold">
          {indicator.indicator_code}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {indicator.indicator_name}
        </Typography>
      </Box>
    </Paper>
  );
}

interface IndicatorSortDialogProps {
  open: boolean;
  onClose: () => void;
  indicators: any[];
  onSave: (newCodes: any[]) => Promise<void>;
  isSaving?: boolean;
}

const IndicatorSortDialog: React.FC<IndicatorSortDialogProps> = ({ 
  open, 
  onClose, 
  indicators, 
  onSave, 
  isSaving = false 
}) => {
  const theme = useTheme();
  const level1Colors = getLevel1Colors(theme);
  const [sortedStructure, setSortedStructure] = useState<any[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeItem, setActiveItem] = useState<any>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 构建初始排序结构
  useEffect(() => {
    if (indicators && indicators.length > 0) {
      const structure = buildInitialStructure(indicators);
      setSortedStructure(structure);
      setHasChanges(false);
    }
  }, [indicators]);

  const handleDragStart = (event: any) => {
    const { active } = event;
    // 找到被拖拽的指标
    const activeItem = findActiveItem(active.id);
    setActiveItem(activeItem);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setSortedStructure((items) => {
        const newStructure = [...items];
        
        // 找到拖拽的指标和目标位置
        const { sourceGroup, sourceIndex, targetGroup, targetIndex } = findDragPositions(
          newStructure, 
          active.id, 
          over.id
        );
        
        if (sourceGroup && targetGroup) {
          // 移动指标
          const movedItem = sourceGroup.indicators.splice(sourceIndex, 1)[0];
          targetGroup.indicators.splice(targetIndex, 0, movedItem);
          setHasChanges(true);
        }
        
        return newStructure;
      });
    }
    
    setActiveItem(null);
  };

  const findActiveItem = (id: string) => {
    for (const level1Group of sortedStructure) {
      for (const level2Group of level1Group.level2Groups) {
        const indicator = level2Group.indicators.find((ind: any) => ind.id === id);
        if (indicator) return indicator;
      }
    }
    return null;
  };

  const findDragPositions = (structure: any[], activeId: string, overId: string) => {
    let sourceGroup: any = null;
    let sourceIndex = -1;
    let targetGroup: any = null;
    let targetIndex = -1;

    for (const level1Group of structure) {
      for (const level2Group of level1Group.level2Groups) {
        const activeIndex = level2Group.indicators.findIndex((ind: any) => ind.id === activeId);
        const overIndex = level2Group.indicators.findIndex((ind: any) => ind.id === overId);
        
        if (activeIndex !== -1) {
          sourceGroup = level2Group;
          sourceIndex = activeIndex;
        }
        if (overIndex !== -1) {
          targetGroup = level2Group;
          targetIndex = overIndex;
        }
      }
    }

    return { sourceGroup, sourceIndex, targetGroup, targetIndex };
  };

  const handleSave = async () => {
    const newCodes = generateNewCodes(sortedStructure);
    await onSave(newCodes);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">指标排序</Typography>
          <Button
            startIcon={<CloseIcon />}
            onClick={onClose}
            disabled={isSaving}
          >
            关闭
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          拖拽指标卡片可以调整指标顺序。排序完成后将自动生成新的指标编码。
        </Alert>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Stack spacing={3}>
            {sortedStructure.map((level1Group) => (
              <Paper
                key={level1Group.id}
                elevation={1}
                sx={{
                  p: 2,
                  backgroundColor: level1Colors[level1Group.name as keyof typeof level1Colors]?.bg,
                  border: `1px solid ${level1Colors[level1Group.name as keyof typeof level1Colors]?.border}`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: level1Colors[level1Group.name as keyof typeof level1Colors]?.text,
                    mb: 2,
                    fontWeight: 'bold',
                  }}
                >
                  {level1Group.name}
                </Typography>

                <Stack spacing={2}>
                  {level1Group.level2Groups.map((level2Group: any) => (
                    <Box key={level2Group.id}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: level1Colors[level1Group.name as keyof typeof level1Colors]?.text,
                          mb: 1,
                          fontWeight: 'medium',
                        }}
                      >
                        {level2Group.name}
                      </Typography>

                      <SortableContext
                        items={level2Group.indicators.map((ind: any) => ind.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Box sx={{ pl: 2 }}>
                          {level2Group.indicators.map((indicator: any) => (
                            <SortableIndicator
                              key={indicator.id}
                              indicator={indicator}
                            />
                          ))}
                        </Box>
                      </SortableContext>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>

          <DragOverlay
            dropAnimation={{
              sideEffects: defaultDropAnimationSideEffects({
                styles: {
                  active: {
                    opacity: '0.5',
                  },
                },
              }),
            }}
          >
            {activeItem ? (
              <Paper
                sx={{
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  opacity: 0.8,
                  transform: 'rotate(5deg)',
                }}
              >
                <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {activeItem.indicator_code}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activeItem.indicator_name}
                  </Typography>
                </Box>
              </Paper>
            ) : null}
          </DragOverlay>
        </DndContext>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          取消
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isSaving}
        >
          {isSaving ? '保存中...' : '保存排序'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IndicatorSortDialog;
